import React, { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { toast } from 'sonner';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import { passCheckins } from '../../../services/passApi';
import { Camera, CheckCircle2, XCircle, Loader2, RotateCw, Package } from 'lucide-react';

export default function PassScanner() {
  const containerId = 'pass-qr-scanner';
  const html5QrRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [manualToken, setManualToken] = useState('');

  useEffect(() => {
    return () => {
      stopScanner();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function startScanner() {
    if (scanning) return;
    setTicket(null);
    setScanning(true);
    try {
      const instance = new Html5Qrcode(containerId);
      html5QrRef.current = instance;
      await instance.start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 250, height: 250 } },
        async (decoded) => {
          await handleToken(decoded);
        },
        () => {}
      );
    } catch (err) {
      toast.error('No se pudo abrir la cámara. Pega el token manualmente.');
      setScanning(false);
    }
  }

  async function stopScanner() {
    if (html5QrRef.current) {
      try {
        await html5QrRef.current.stop();
        await html5QrRef.current.clear();
      } catch (_) {}
      html5QrRef.current = null;
    }
    setScanning(false);
  }

  async function handleToken(token) {
    const cleaned = (token || '').trim();
    if (!cleaned) return;
    setBusy(true);
    await stopScanner();
    try {
      const res = await passCheckins.scan(cleaned);
      setTicket(res.data.reservation);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Ticket inválido');
    } finally {
      setBusy(false);
    }
  }

  async function confirmCheckin() {
    if (!ticket) return;
    setBusy(true);
    try {
      const res = await passCheckins.confirm({
        qr_token: ticket.qr_token,
        benefit_delivered: true,
      });
      toast.success('Check-in confirmado');
      setTicket({
        ...res.data.reservation,
        commission_preview: res.data.commission?.net_affiliate_commission,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo confirmar');
    } finally {
      setBusy(false);
    }
  }

  function reset() {
    setTicket(null);
    setManualToken('');
  }

  return (
    <DashboardLayout>
      <div className="px-6 py-6 max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-900 mb-2">Scanner Pass</h1>
        <p className="text-sm text-slate-500 mb-6">Escanea el QR del cliente para validar la promo.</p>

        <div className="bg-white border border-slate-200 rounded-2xl p-5 shadow-sm">
          {!ticket && (
            <>
              <div id={containerId} className="w-full max-w-sm mx-auto" />
              <div className="flex gap-2 mt-4">
                {scanning ? (
                  <button
                    onClick={stopScanner}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-slate-900 text-white font-semibold"
                  >
                    Detener cámara
                  </button>
                ) : (
                  <button
                    onClick={startScanner}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-teal-600 hover:bg-teal-700 text-white font-semibold"
                  >
                    <Camera className="w-4 h-4" /> Iniciar cámara
                  </button>
                )}
              </div>
              <div className="mt-4 border-t pt-4">
                <p className="text-xs text-slate-500 mb-2">¿No funciona la cámara? Pega el token manualmente:</p>
                <div className="flex gap-2">
                  <input
                    className="flex-1 border border-slate-300 rounded-lg px-3 py-2 font-mono text-sm"
                    placeholder="qr_token"
                    value={manualToken}
                    onChange={(e) => setManualToken(e.target.value)}
                  />
                  <button
                    onClick={() => handleToken(manualToken)}
                    disabled={busy || !manualToken}
                    className="px-4 py-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold disabled:opacity-60"
                  >
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Validar'}
                  </button>
                </div>
              </div>
            </>
          )}

          {ticket && (
            <div>
              <div className="flex items-start justify-between">
                <div>
                  <h2 className="text-lg font-bold text-slate-900">{ticket.offer_title}</h2>
                  <p className="text-sm text-slate-500">
                    {ticket.customer_name || 'Cliente'} · {ticket.people_count} pers
                  </p>
                </div>
                {ticket.checkin_status === 'checked_in' ? (
                  <CheckCircle2 className="w-8 h-8 text-green-500" />
                ) : (
                  <XCircle className="w-8 h-8 text-amber-500" />
                )}
              </div>

              <div className="mt-4 space-y-2 text-sm">
                {ticket.benefits?.map((b, idx) => (
                  <Row key={idx} label="Beneficio" value={b.name} />
                ))}
                <Row label="Pagado en app" value={`$${Number(ticket.paid_in_app || 0).toFixed(2)}`} />
                <Row label="Pendiente en negocio" value={`$${Number(ticket.pending_at_business || 0).toFixed(2)}`} />
                {ticket.affiliate_name && <Row label="Afiliado" value={ticket.affiliate_name} />}
                {typeof ticket.commission_preview === 'number' && (
                  <Row label="Comisión" value={`$${ticket.commission_preview.toFixed(2)}`} />
                )}
                <Row label="Estado" value={ticket.checkin_status} />
              </div>

              {(ticket.included_items?.length > 0) && (
                <div className="mt-4 p-3 rounded-lg bg-teal-50 border border-teal-200">
                  <p className="text-xs font-semibold uppercase tracking-wide text-teal-700 mb-1 inline-flex items-center gap-1">
                    <Package className="w-3.5 h-3.5" /> Incluido
                  </p>
                  <ul className="space-y-0.5">
                    {ticket.included_items.map((it, idx) => (
                      <li key={idx} className="text-xs text-slate-700">• {it.name}{it.description ? ` — ${it.description}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}

              {(ticket.addons_selected?.length > 0) && (
                <div className="mt-3 p-3 rounded-lg bg-orange-50 border border-orange-200">
                  <p className="text-xs font-semibold uppercase tracking-wide text-orange-700 mb-1 inline-flex items-center gap-1">
                    <Package className="w-3.5 h-3.5" /> Adicionales contratados
                  </p>
                  <ul className="space-y-0.5">
                    {ticket.addons_selected.map((a, idx) => (
                      <li key={idx} className="text-xs text-slate-700 flex justify-between">
                        <span>• {a.name} × {a.qty}</span>
                        <span className="font-mono">${Number(a.subtotal || 0).toFixed(2)} {a.pay_mode === 'pay_at_business' ? '(cobrar)' : ''}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex gap-2 mt-5">
                {ticket.checkin_status !== 'checked_in' && (
                  <button
                    onClick={confirmCheckin}
                    disabled={busy}
                    className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg bg-green-500 hover:bg-green-600 text-white font-semibold disabled:opacity-60"
                  >
                    {busy && <Loader2 className="w-4 h-4 animate-spin" />} Confirmar check-in
                  </button>
                )}
                <button
                  onClick={reset}
                  className="px-4 py-3 rounded-lg border border-slate-300 hover:bg-slate-50 text-slate-700 font-semibold"
                >
                  <RotateCw className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <span className="text-slate-500">{label}</span>
      <span className="text-slate-900 text-right font-medium">{value}</span>
    </div>
  );
}
